import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Wallet, Power } from 'lucide-react'
import { toast } from 'sonner'

import {
  affiliates,
  type Affiliate,
  type LedgerEntry,
  type Payout,
} from '../api/endpoints'
import { Button } from '../components/Button'
import { Card, CardContent } from '../components/Card'
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input, Select } from '../components/Input'
import { PageLoader } from '../components/Spinner'
import { AffiliateStatusBadge } from '../components/StatusBadge'
import { formatDateTime, formatINR, formatPhone } from '../lib/format'

export function AffiliateDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null)
  const [ledger, setLedger] = useState<LedgerEntry[]>([])
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [loading, setLoading] = useState(true)

  function reload() {
    if (!id) return
    const aid = Number(id)
    setLoading(true)
    Promise.all([affiliates.detail(aid), affiliates.ledger(aid)])
      .then(([d, l]) => {
        setAffiliate(d.affiliate)
        setLedger(l.ledger)
        setPayouts(l.payouts)
      })
      .catch((err) => toast.error('Failed to load', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [id])

  if (loading || !affiliate) return <PageLoader />

  return (
    <div className="space-y-5 max-w-6xl">
      <Link
        to="/affiliates"
        className="text-sm text-slatey hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="size-3.5" /> Back to affiliates
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-semibold text-ink">{affiliate.name ?? 'Affiliate'}</h1>
            <AffiliateStatusBadge status={affiliate.status} />
          </div>
          <p className="text-sm text-slatey mt-1">
            {formatPhone(affiliate.phone)} · {affiliate.email ?? 'no email'}
          </p>
          <p className="mt-2 font-mono text-sm bg-ivory inline-block px-2 py-1 rounded border border-line">
            {affiliate.referralCode}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusDialog affiliate={affiliate} onChanged={reload} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard label="Total earned" value={formatINR(affiliate.totalEarned)} />
        <KpiCard label="Total paid" value={formatINR(affiliate.totalPaid)} />
        <KpiCard label="Pending payout" value={formatINR(affiliate.pendingPayout)} highlight={affiliate.pendingPayout > 0} />
        <KpiCard label="Commission rate" value={`${affiliate.commissionRate}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-ink">Ledger</h2>
            <span className="text-xs text-slatey">{ledger.length} entries</span>
          </div>
          {ledger.length === 0 ? (
            <CardContent>
              <p className="text-sm text-slatey text-center py-8">No commission entries yet.</p>
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-ivory">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                      When
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                      Order
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                      Type
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slatey">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((l) => (
                    <tr key={l.id} className="border-t border-line">
                      <td className="px-4 py-2.5 text-xs text-slatey">{formatDateTime(l.createdAt)}</td>
                      <td className="px-4 py-2.5 text-sm font-mono text-ink">{l.orderNumber}</td>
                      <td className="px-4 py-2.5 text-xs capitalize text-slatey">{l.type}</td>
                      <td
                        className={`px-4 py-2.5 text-sm text-right font-medium ${
                          l.amount < 0 ? 'text-danger' : 'text-forest'
                        }`}
                      >
                        {l.amount >= 0 ? '+' : ''}
                        {formatINR(l.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <h2 className="font-display text-base font-semibold text-ink">Payouts</h2>
            <RecordPayoutDialog affiliateId={affiliate.id} onCreated={reload} disabled={affiliate.status !== 'active' || affiliate.pendingPayout <= 0} />
          </div>
          {payouts.length === 0 ? (
            <CardContent>
              <p className="text-sm text-slatey text-center py-8">No payouts recorded yet.</p>
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-ivory">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                      When
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slatey">
                      Reference
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slatey">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-t border-line">
                      <td className="px-4 py-2.5 text-xs text-slatey">{formatDateTime(p.createdAt)}</td>
                      <td className="px-4 py-2.5 text-xs font-mono text-ink">{p.reference ?? '—'}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium">{formatINR(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

function KpiCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Card className="p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-slatey">{label}</p>
      <p className={`mt-2 font-display text-xl font-semibold ${highlight ? 'text-amber' : 'text-ink'}`}>{value}</p>
    </Card>
  )
}

function StatusDialog({
  affiliate,
  onChanged,
}: {
  affiliate: Affiliate
  onChanged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(affiliate.status)
  const [saving, setSaving] = useState(false)

  async function apply() {
    setSaving(true)
    try {
      await affiliates.update(affiliate.id, { status })
      toast.success('Status updated')
      setOpen(false)
      onChanged()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Power className="size-3.5" /> Change status
        </Button>
      </DialogTrigger>
      <DialogContent title="Change affiliate status">
        <FormField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as Affiliate['status'])}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </Select>
        </FormField>
        <div className="mt-5 flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={apply} loading={saving} size="sm">
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function RecordPayoutDialog({
  affiliateId,
  onCreated,
  disabled,
}: {
  affiliateId: number
  onCreated: () => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await affiliates.recordPayout(affiliateId, {
        amount: Number(amount),
        reference: reference.trim() || undefined,
        note: note.trim() || undefined,
      })
      toast.success('Payout recorded')
      setOpen(false)
      setAmount('')
      setReference('')
      setNote('')
      onCreated()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Wallet className="size-3.5" /> Record payout
        </Button>
      </DialogTrigger>
      <DialogContent title="Record payout" description="Deducts from the affiliate's pending balance and increments total paid.">
        <form onSubmit={onSubmit} className="space-y-3">
          <FormField label="Amount (₹)" required hint="Rupees, not paise">
            <Input
              type="number"
              min={1}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              autoFocus
            />
          </FormField>
          <FormField label="Reference" hint="UPI txn id or bank reference">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UPI/20260515/1234567" />
          </FormField>
          <FormField label="Note">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={saving} size="sm">
              Record
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}